import { useState, useCallback, useRef, useEffect } from 'react';
import {
  startEmailClaim,
  verifyEmailClaim,
  startEmailSignIn,
  verifyEmailSignIn,
  getSession,
  signOut,
  setMyDisplayName,
  getMyDisplayName,
  ensureAnonymousSession,
  startPhoneSignIn,
  verifyPhoneSignIn,
} from '../api/supabase.js';
import { toE164, formatPhone, phoneCodeMessage, isUnknownPhone } from '../data/signInPhone.js';
import { lastSignInMethod, rememberSignInMethod, queuePhoneCard, signInFormOpened } from './account/phoneCardQueue.js';

/**
 * LoginForm — the reusable inner login/claim surface.
 *
 * Email-first (Axis 2, A2-2): one email field → send OTP regardless → branch
 * new-vs-existing post-auth (server-side). No pre-check for account existence
 * (prevents account enumeration). v1 = magic-link + 6-digit OTP only.
 *
 * Steps:
 *   email → verify → name (any account without one, required) → success
 *   phone → verify → name → success      a code by text, for an account with a phone on it
 *   phone → phone-add-email → verify → name? → (the phone card, after the form)
 *                                        a number on no account yet: email first, the phone after
 *
 * By text is sign-in only: Supabase is asked never to create an account from a phone, and a database
 * hook refuses one if anything else asks. So a number on no account cannot sign in by itself. The
 * person asked for text, though, and usually believes their phone is already "in Arcos" because they
 * gave it to a space, so they are not sent back to start again: the same card asks for their email
 * ('phone-add-email') and keeps the number they typed.
 *
 * EMAIL FINISHES FIRST, AND THE PHONE IS A SEPARATE CARD. Once the email code lands, the form asks
 * for the card — "You're signed in. Want to sign in by text next time? We'll text (914) 263-7495" —
 * and leaves. Nothing is texted until they tap. An email code followed at once by an unasked text
 * code read as one long, strange sign-in (Wendy's and Ben's tests, October 2026). The card is not a
 * step of this form because many hosts take the form away the moment someone is signed in — a
 * members-only space's sign-in page reloads into the space — so it never showed there; it is drawn
 * above every app by PhoneCardAfterSignIn, from the note in account/phoneCardQueue.js. After any
 * other email sign-in, an account with no phone is offered the same card with an empty phone box,
 * once per account, a brand-new account included once it has its name. In the apps (`compact`) it
 * is offered only when they typed a number, because they asked for text.
 * Plan: vault "Claiming a Profile by Text — Phase Two Build Plan", Phase 1.
 *
 * The name step appears AFTER OTP verification — we don't know whether the user
 * is new or existing until the server responds (email-first), so we can't ask
 * for it upfront without leaking account existence.
 *
 * WHO IS ASKED is the account's own `display_name`, not how it got here
 * (2026-09-15). A new account never has one, so a claim always asks. A
 * returning account normally does, and skips. What used to fall between them is
 * an account that exists with the column empty — minted server-side for a
 * ticket holder, or made before a name was required — which signed in, was
 * never asked, and stayed nameless: the state the required name exists to
 * prevent, since a member with no name is the reason `space_members()` would
 * have to hand out email addresses instead.
 *
 * Props:
 *   onSuccess  — called after successful auth (session is live)
 *   onCancel   — optional; called when user cancels (modal close / back)
 *   returnTo   — optional URL to redirect to after success
 *   spaceName  — optional space display name for copy
 *   compact    — optional; skip the success step (close immediately on verify)
 *   onStepChange — optional; called with the current step, so a host that draws
 *                  its own preamble can drop it once the form has moved on
 */

const SUCCESS_DISMISS_MS = 4000;

// Which way this device signed in last, so someone who uses text opens on the phone box next time.
const lastMethod = lastSignInMethod;
const rememberMethod = rememberSignInMethod;
// The offer to add a phone is made once per account on this device, and "Not now" is an answer.
const OFFER_KEY = 'arcos-phone-offer-seen:';
const offerSeen = (id) => { try { return !!localStorage.getItem(OFFER_KEY + id); } catch { return true; } };
const markOfferSeen = (id) => { try { localStorage.setItem(OFFER_KEY + id, '1'); } catch { /* storage off */ } };

/**
 * Two hosting props, both off by default so nothing in the main app changes:
 *   codeOnly  — the copy names only the six-digit code, never the link. The Climate Week app decided
 *               (2026-09-14) the code is its only path, because typing it is what merges a guest's
 *               saves into an existing account and clicking the link loses them.
 *   hideIntro — omit the email step's own heading and subtitle, for a host dialog that already
 *               says why you are signing in. The verify and name steps keep their headings.
 *   existingOnly — sign in to an account that exists, never create one. An unknown address is
 *               answered under the box and reported through `onUnknownEmail(address)`.
 *   channels  — which box a space's door asks for, its "People sign in with" (src/features/door/
 *               doorModel.js): 'email' opens on email and offers no text, 'phone' opens on the phone
 *               box with email one tap away, 'either' (the default) opens on whichever this device
 *               used last, with the other one tap away — as every other host has always had it.
 */
export default function LoginForm({ onSuccess, onCancel, returnTo, spaceName, compact = false, codeOnly = false, hideIntro = false, onRequireComplete, onStepChange, ticketEventSlug, existingOnly = false, onUnknownEmail = null, channels = 'either' }) {
  // 'email' | 'phone' | 'phone-add-email' | 'verify' | 'name' | 'success'
  const [step, setStep] = useState(() => (channels === 'email' ? 'email' : channels === 'phone' ? 'phone' : lastMethod()));
  const [mode, setMode] = useState('claim'); // 'claim' (new) | 'signin' (existing)
  // How the code was sent: by email, or by text to `phoneE164`.
  const [channel, setChannel] = useState('email');
  const [phone, setPhone] = useState('');
  const [phoneE164, setPhoneE164] = useState(null);
  // A number typed to sign in with that no account has yet: added to whichever account the email
  // code then signs in to, without being typed again.
  const [pendingPhone, setPendingPhone] = useState(null);
  // The signed-in account, once verified: whether to offer adding a phone depends on it.
  const signedIn = useRef(null);
  // Whether this sign-in had to ask for a name. An invited account is minted before the person ever
  // arrives, so its first sign-in is a 'signin', not a 'claim' — and it was greeted "Welcome back!"
  // on the day it first saw Arcos. Having no name yet is the honest sign that this is an arrival.
  const [askedName, setAskedName] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(SUCCESS_DISMISS_MS / 1000);
  const inputRef = useRef(null);
  const dismissTimer = useRef(null);
  const tickTimer = useRef(null);

  // Auto-focus the active input on step change
  useEffect(() => { inputRef.current?.focus(); }, [step]);

  // While this form is on screen the phone card waits (account/phoneCardQueue.js).
  useEffect(() => signInFormOpened(), []);
  // Whether this sign-in asked for the phone card, decided the moment the code was accepted.
  const askedForCard = useRef(false);

  // Tell the host which step is on screen. A dialog that supplies its own title and value copy shows
  // it over the FIRST step and not over the rest: past the email box, the step's own heading is the
  // screen. Nothing else in the app passes this, so nothing else changes.
  useEffect(() => { onStepChange?.(step); }, [step, onStepChange]);

  // Signal to parent whether we're on a required step that must be completed
  // (the name step). This prevents the modal from being dismissed. The step is
  // only ever reached when the account has no name, so reaching it IS the
  // requirement — a returning account filling in a missing one is as bound by it
  // as a new account giving its first.
  useEffect(() => {
    const required = step === 'name';
    onRequireComplete?.(required);
    return () => onRequireComplete?.(false);
  }, [step, mode, onRequireComplete]);

  // Success auto-dismiss countdown
  useEffect(() => {
    if (step !== 'success') return;
    dismissTimer.current = setTimeout(() => {
      onSuccess?.({ isNewUser: mode === 'claim' });
    }, SUCCESS_DISMISS_MS);
    tickTimer.current = setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => {
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
      if (tickTimer.current) clearInterval(tickTimer.current);
    };
  }, [step, mode, onSuccess]);

  // ── Email submit: send OTP (email-first, no pre-check) ──────────
  const handleSendCode = useCallback(async (e) => {
    e.preventDefault();
    const addr = email.trim();
    if (!addr || loading) return;
    setLoading(true);
    setError(null);
    try {
      // Ticket-aware unblock: on a ticketed space, a completed ticket-holder's account was minted
      // server-side as UNCLAIMED (unconfirmed email) — the generic claim/sign-in below can't sign it
      // in. claim-signin confirms that account + provisions membership when a real ticket exists (no-op
      // otherwise, non-enumerable). Must run before the sign-in attempt. See api/tickets/claim-signin.js.
      if (ticketEventSlug) {
        try {
          await fetch('/api/tickets/claim-signin', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: addr, eventSlug: ticketEventSlug }),
          });
        } catch { /* non-fatal — fall through to the normal claim/sign-in flow */ }
      }
      // If a real account is already signed in, sign out to a fresh anonymous
      // session first — so the entered email is a clean claim or sign-in,
      // never an email-change on the current account.
      const sess = await getSession();
      if (sess?.user && sess.user.is_anonymous === false) {
        await signOut();
      } else if (!sess) {
        // Bare mounts (/login reached from the public landing page) never ran
        // WorkspaceRouter's ensureAnonymousSession — but startEmailClaim claims
        // the CURRENT session (auth.updateUser), so without one it throws
        // "Auth session missing!". Mint the anonymous session first.
        await ensureAnonymousSession();
      }
      // Sign-in only (a members-only space's front door): never mint an account for a typed address.
      // A typo there was a brand-new stranger account that then hit "not found" — so an address with
      // no account is said plainly, and the host can offer another way in (onUnknownEmail).
      if (existingOnly) {
        try {
          await startEmailSignIn(addr);
        } catch (err) {
          if (/signups? not allowed|user not found|not.*(exist|found)/i.test(err.message || '')) {
            setError('We don’t have that email. Try the one you registered with.');
            onUnknownEmail?.(addr);
            setLoading(false);
            return;
          }
          throw err;
        }
        setMode('signin');
        setStep('verify');
        setLoading(false);
        return;
      }
      // Try claim first (re-binds the anonymous UUID → preserves any work).
      const { confirmed } = await startEmailClaim(addr);
      if (confirmed) {
        // Local dev auto-confirm: claim already complete → need name
        setMode('claim');
        setStep('name');
        setLoading(false);
        return;
      }
      setMode('claim');
      setStep('verify');
      setLoading(false);
    } catch (err) {
      // "Already registered" → returning user: sign into the existing account.
      const exists = /already.*(regist|exist|been|in use)|email.*exists/i.test(err.message || '');
      if (exists) {
        try {
          await startEmailSignIn(addr);
          setMode('signin');
          setStep('verify');
          setLoading(false);
        } catch (err2) {
          setError(err2.message || 'Could not send sign-in code. Try again.');
          setLoading(false);
        }
      } else {
        setError(err.message || 'Something went wrong. Try again.');
        setLoading(false);
      }
    }
  }, [email, loading, ticketEventSlug, existingOnly, onUnknownEmail]);

  // ── Phone submit: text a code to an account that has this number ─
  const handleSendPhoneCode = useCallback(async (e) => {
    e.preventDefault();
    if (loading) return;
    const e164 = toE164(phone);
    if (!e164) { setError('Enter a US or Canadian number.'); return; }
    setLoading(true);
    setError(null);
    try {
      // As for email: a real account signed in here is signed out first, and a bare mount gets the
      // anonymous session whose saves the sign-in then keeps.
      const sess = await getSession();
      if (sess?.user && sess.user.is_anonymous === false) await signOut();
      else if (!sess) await ensureAnonymousSession();
      await startPhoneSignIn(e164);
      setPhoneE164(e164);
      setChannel('phone');
      setMode('signin');
      setCode('');
      setStep('verify');
    } catch (err) {
      if (isUnknownPhone(err)) { setPendingPhone(e164); setStep('phone-add-email'); }
      else setError(phoneCodeMessage(err, 'signin'));
    }
    setLoading(false);
  }, [phone, loading]);

  // ── OTP verification ────────────────────────────────────────────
  const handleVerify = useCallback(async (e) => {
    e.preventDefault();
    if (!code.trim() || loading) return;
    setLoading(true);
    setError(null);
    try {
      let verified;
      if (channel === 'phone') verified = await verifyPhoneSignIn(phoneE164, code.trim());
      else if (mode === 'signin') verified = await verifyEmailSignIn(email.trim(), code.trim());
      else verified = await verifyEmailClaim(email.trim(), code.trim());
      signedIn.current = verified?.user || null;
      rememberMethod(channel);
      // Before any other await: a host that reloads on sign-in leaves no time for later.
      askedForCard.current = askForPhoneCard();

      // A name is needed when the account does not already have one. A claim never does — it mints
      // the account here — so it asks without a read; a returning account is asked only if
      // `accounts.display_name` is empty, which is the "already has a name skips this step" rule
      // written the way it was always meant. The read is the session's own (actor_labels with the
      // caller's uuid), so it costs one round trip on the sign-in path and none on the claim path.
      let needsName = mode === 'claim';
      if (!needsName) {
        // Wrapped, because verification has already LANDED by this point: a failed read must not
        // fall into the catch below and be reported as a bad code, which would be a dead end — the
        // code is spent and retyping it cannot work. An unreadable name counts as a name, so the
        // worst a failure costs is a prompt that Me can give later.
        try { needsName = !(await getMyDisplayName()); } catch { needsName = false; }
      }
      if (needsName) {
        setAskedName(true);
        setStep('name');
        setLoading(false);
      } else {
        // Returning user with a name — skip the step, go straight to success
        if (askedForCard.current) { leaveForPhoneCard(); return; }
        if (compact) { onSuccess?.({ isNewUser: false }); return; }
        enterSuccess();
      }
    } catch (err) {
      setError(channel === 'phone' ? phoneCodeMessage(err, 'signin') : (err.message || 'Invalid or expired code. Try again.'));
      setLoading(false);
    }
  }, [code, email, loading, mode, channel, phoneE164, pendingPhone, onSuccess, compact]);

  // ── Name submit (any account arriving without one) ──────────────
  const handleNameSubmit = useCallback(async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || loading) return;
    setLoading(true);
    setError(null);
    try {
      await setMyDisplayName(trimmed);
      if (askedForCard.current) { leaveForPhoneCard(); return; }
      if (compact) {
        // Reaching this step does not make the account new: a returning account that never had a
        // name is asked here too, and it has no welcome coming. `enterSuccess` below reads the same
        // fact the same way.
        if (mode === 'claim') {
          try { sessionStorage.setItem('welcomeToast', '1'); } catch { /* ignore */ }
        }
        onSuccess?.({ isNewUser: mode === 'claim' });
        return;
      }
      enterSuccess();
    } catch (err) {
      setError(err.message || 'Could not save your name. Try again.');
      setLoading(false);
    }
  }, [name, loading, mode, pendingPhone, onSuccess, compact]);

  /**
   * After an email sign-in, ask for the phone card (account/phoneCardQueue.js), which
   * PhoneCardAfterSignIn draws on the page the person lands on once this form is gone — after the
   * name step, for a new account. Called the moment the code is accepted, with no await before it.
   * Returns whether it was asked for.
   *
   * Never for an account that already has a phone: replacing someone's sign-in phone is Account
   * settings' job, with its own words. With a number they typed asking for text, always, the apps
   * included, because that is what brought them here. Without one, once per account and not in the
   * apps, which close the moment the person is in.
   */
  function askForPhoneCard() {
    const u = signedIn.current;
    if (channel !== 'email' || !u?.id) return false;
    if (u.phone && u.phone_confirmed_at) return false;
    if (!pendingPhone && (compact || offerSeen(u.id))) return false;
    markOfferSeen(u.id);
    queuePhoneCard({ accountId: u.id, phone: pendingPhone });
    return true;
  }

  /** The card says "You're signed in", so the form skips its own success screen and hands back. */
  function leaveForPhoneCard() {
    if (mode === 'claim') {
      try { sessionStorage.setItem('welcomeToast', '1'); } catch { /* ignore */ }
    }
    setLoading(false);
    onSuccess?.({ isNewUser: mode === 'claim' });
  }

  function enterSuccess() {
    // ONB-4/ONB-6: set welcome toast flag for new users.
    if (mode === 'claim') {
      try { sessionStorage.setItem('welcomeToast', '1'); } catch { /* ignore */ }
    }
    setSecondsLeft(SUCCESS_DISMISS_MS / 1000);
    setStep('success');
    setLoading(false);
  }

  function continueNow() {
    if (dismissTimer.current) clearTimeout(dismissTimer.current);
    if (tickTimer.current) clearInterval(tickTimer.current);
    onSuccess?.({ isNewUser: mode === 'claim' });
  }

  // ── Render ──────────────────────────────────────────────────────

  if (step === 'success') {
    // The greeting, not the welcome toast: `enterSuccess` still sets that for a claim alone.
    const isNewUser = mode === 'claim' || askedName;
    return (
      <div className="login-form">
        <div className="login-form__success">
          <div className="login-form__success-icon">
            {isNewUser ? '🎉' : '👋'}
          </div>
          <h2 className="login-form__title">
            {isNewUser ? `Welcome, ${name}!` : 'Welcome back!'}
          </h2>
          <p className="login-form__subtitle">
            {isNewUser
              ? (spaceName
                  ? <>You&rsquo;re now signed in to <strong>{spaceName}</strong>.</>
                  : 'Your account is set up and ready to go.')
              : (spaceName
                  ? <>Signed in to <strong>{spaceName}</strong>.</>
                  : 'You\u2019re signed in.')}
          </p>
          <button
            className="login-form__btn login-form__btn--primary"
            type="button"
            onClick={continueNow}
          >
            Continue ({secondsLeft}s)
          </button>
        </div>
      </div>
    );
  }

  if (step === 'name') {
    return (
      <div className="login-form">
        <h2 className="login-form__title">What&rsquo;s your name?</h2>
        <p className="login-form__subtitle">
          {spaceName
            ? <>This is how other people in <strong>{spaceName}</strong> will see you.</>
            : 'This is how other people on Arcos will see you.'}
        </p>
        <form onSubmit={handleNameSubmit} className="login-form__fields">
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your full name"
            className="login-form__input"
            disabled={loading}
            autoComplete="name"
            required
          />
          {error && <div className="login-form__error">{error}</div>}
          <button
            className="login-form__btn login-form__btn--primary"
            type="submit"
            disabled={!name.trim() || loading}
          >
            {loading ? 'Saving\u2026' : 'Continue'}
          </button>
        </form>
      </div>
    );
  }

  if (step === 'phone-add-email') {
    return (
      <div className="login-form">
        <p className="login-form__subtitle">
          You don’t have a phone number associated with your Arcos account. First, <strong>sign in with a
          code to your email.</strong> After that, you can add {formatPhone(pendingPhone)} with one tap.
        </p>
        <p className="login-form__subtitle login-form__subtitle--note">
          A phone you gave a space is separate. This one is only for sign-in codes.
        </p>
        <form onSubmit={handleSendCode} className="login-form__fields">
          <input
            ref={inputRef}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="login-form__input"
            disabled={loading}
            autoComplete="email"
            aria-label="Your email"
            required
          />
          {error && <div className="login-form__error">{error}</div>}
          <button className="login-form__btn login-form__btn--primary" type="submit" disabled={!email.trim() || loading}>
            {loading ? 'Sending\u2026' : 'Email me a code'}
          </button>
          <button type="button" className="login-form__btn login-form__btn--link" disabled={loading}
            onClick={() => { setPendingPhone(null); setStep('phone'); setError(null); }}>
            ← Use a different number
          </button>
          {onCancel && (
            <button type="button" className="login-form__btn login-form__btn--link" onClick={onCancel} disabled={loading}>Cancel</button>
          )}
        </form>
      </div>
    );
  }

  if (step === 'phone') {
    return (
      <div className="login-form">
        {!hideIntro && (
          <>
            <h2 className="login-form__title">{spaceName ? `Welcome to ${spaceName}` : 'Sign in by text'}</h2>
            <p className="login-form__subtitle">Enter your mobile number. We’ll text you a six-digit code.</p>
          </>
        )}
        <form onSubmit={handleSendPhoneCode} className="login-form__fields">
          <input
            ref={inputRef}
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => { setPhone(e.target.value); setError(null); }}
            placeholder="(415) 555-0100"
            className="login-form__input"
            disabled={loading}
            autoComplete="tel"
            aria-label="Your phone number, US or Canada"
          />
          {error && <div className="login-form__error">{error}</div>}
          <button className="login-form__btn login-form__btn--primary" type="submit" disabled={!phone.trim() || loading}>
            {loading ? 'Sending\u2026' : 'Text me a code'}
          </button>
          <button type="button" className="login-form__btn login-form__btn--link"
            onClick={() => { setStep('email'); setError(null); rememberMethod('email'); }} disabled={loading}>
            Email me a code instead
          </button>
          {onCancel && (
            <button type="button" className="login-form__btn login-form__btn--link" onClick={onCancel} disabled={loading}>Cancel</button>
          )}
        </form>
      </div>
    );
  }

  if (step === 'verify' && channel === 'phone') {
    return (
      <div className="login-form">
        <h2 className="login-form__title">Check your phone</h2>
        <p className="login-form__subtitle">
          We texted a six-digit code to <strong>{formatPhone(phoneE164)}</strong>. Enter it below.
        </p>
        <form onSubmit={handleVerify} className="login-form__fields">
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="6-digit code"
            className="login-form__input login-form__input--code"
            disabled={loading}
            autoComplete="one-time-code"
            maxLength={6}
          />
          {error && <div className="login-form__error">{error}</div>}
          <button className="login-form__btn login-form__btn--primary" type="submit" disabled={code.trim().length < 6 || loading}>
            {loading ? 'Verifying\u2026' : 'Verify code'}
          </button>
          <button type="button" className="login-form__btn login-form__btn--link" disabled={loading}
            onClick={async () => {
              setError(null);
              try { await startPhoneSignIn(phoneE164); setCode(''); setError('Code resent — check your messages.'); }
              catch (err) { setError(phoneCodeMessage(err, 'signin')); }
            }}>
            Resend code
          </button>
          <button type="button" className="login-form__btn login-form__btn--link" disabled={loading}
            onClick={() => { setStep('phone'); setChannel('email'); setCode(''); setError(null); }}>
            ← Use a different number
          </button>
        </form>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div className="login-form">
        <h2 className="login-form__title">Check your email</h2>
        <p className="login-form__subtitle">
          {codeOnly ? (
            <>We sent a six-digit code to <strong>{email}</strong>. Enter it below.</>
          ) : (
            <>
              We sent a magic link and a 6-digit code to{' '}
              <strong>{email}</strong>. Click the link in the email, or enter
              the code below.
            </>
          )}
        </p>
        <form onSubmit={handleVerify} className="login-form__fields">
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="6-digit code"
            className="login-form__input login-form__input--code"
            disabled={loading}
            autoComplete="one-time-code"
            maxLength={6}
          />
          {error && <div className="login-form__error">{error}</div>}
          <button
            className="login-form__btn login-form__btn--primary"
            type="submit"
            disabled={code.trim().length < 6 || loading}
          >
            {loading ? 'Verifying\u2026' : 'Verify code'}
          </button>
          <button
            type="button"
            className="login-form__btn login-form__btn--link"
            onClick={async () => {
              setError(null);
              try {
                if (mode === 'signin') await startEmailSignIn(email.trim());
                else await startEmailClaim(email.trim());
                setCode('');
                setError('Code resent — check your email.');
              } catch (err) {
                // Supabase GoTrue enforces a ~60s rate limit between OTP sends.
                // Rewrite the raw server message into something friendlier.
                const msg = err.message || '';
                if (/security purposes|rate.?limit|too many|try again/i.test(msg)) {
                  const secs = msg.match(/after (\d+) seconds/)?.[1];
                  setError(secs
                    ? `Please wait ${secs} seconds before resending.`
                    : 'Please wait a moment before resending.');
                } else {
                  setError(msg || 'Could not resend code. Try again.');
                }
              }
            }}
            disabled={loading}
          >
            Resend code
          </button>
          <button
            type="button"
            className="login-form__btn login-form__btn--link"
            onClick={() => { setStep(pendingPhone ? 'phone-add-email' : 'email'); setCode(''); setError(null); }}
            disabled={loading}
          >
            ← Use a different email
          </button>
        </form>
      </div>
    );
  }

  // Step: email (default)
  return (
    <div className="login-form">
      {!hideIntro && (
        <>
          <h2 className="login-form__title">
            {spaceName ? `Welcome to ${spaceName}` : 'Welcome'}
          </h2>
          <p className="login-form__subtitle">
            {existingOnly ? 'Enter the email on your Arcos account.' : 'Enter your email to sign in or create an account.'}
            {codeOnly ? ' We’ll email you a six-digit code.' : ' We’ll send you a magic link and a code.'}
          </p>
        </>
      )}
      <form onSubmit={handleSendCode} className="login-form__fields">
        <input
          ref={inputRef}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="login-form__input"
          disabled={loading}
          autoComplete="email"
          required
        />
        {error && <div className="login-form__error">{error}</div>}
        <button
          className="login-form__btn login-form__btn--primary"
          type="submit"
          disabled={!email.trim() || loading}
        >
          {loading ? 'Sending\u2026' : (codeOnly ? 'Email me a code' : 'Continue with email')}
        </button>
        {channels !== 'email' && (
          <button
            type="button"
            className="login-form__btn login-form__btn--link"
            onClick={() => { setStep('phone'); setError(null); }}
            disabled={loading}
          >
            Text me a code instead
          </button>
        )}
        {onCancel && (
          <button
            type="button"
            className="login-form__btn login-form__btn--link"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
        )}
      </form>
    </div>
  );
}
